import { ConfiguratorType, OrderType } from "@biz/Definitions";
import { RemoteChannelCount } from "@biz/definitions/MountDefinitons";
import { Product } from "@biz/definitions/Product";
import { Curtain2dLogic } from "@biz/items/curtain/Curtain2dLogic";
import { CurtainLogic } from "@biz/items/curtain/CurtainLogic";
import { RaffLogic } from "@biz/items/raff/RaffLogic";
import { SunSailLogic } from "@biz/items/sunSail/logic/SunSailLogic";
import { getChecked } from "@biz/logic/getChecked";
import { MotorLogic } from "@biz/logic/MotorLogic";
import { PD } from "@biz/PD";
import { IProductDescriptor } from "@biz/ProductDescriptor";
import { ProductData } from "@biz/projectCalculation/ProjectDefinitions";

export class ProductLogic {
  static calcClothAreaToPay(productData: ProductData | undefined): number {
    if (productData) {
      if (Product.isSunSail(productData)) {
        return SunSailLogic.calcSunSailAreaToPay(productData);
      } else if (Product.isRaff(productData)) {
        const sailSize = RaffLogic.calcClothSize(productData)
        return sailSize.w * sailSize.h;
      } else if (Product.isCurtain(productData)) {
        return CurtainLogic.calcTotalNetArea(productData);
      } else if (Product.isCurtain2d(productData)) {
        return Curtain2dLogic.calcTotalNetArea(productData);
      }
    }
    return 0;
  }

  static getOrderType(product: ProductData, configuratorType: ConfiguratorType): OrderType | undefined {
    return getChecked({
      selection: product.orderType || Product.calcOrderType(PD.productToPD(product), configuratorType).initial,
      validItems: ProductLogic.getPossibleOrderTypes(product, configuratorType),
      emptySelection: undefined// "__selectionRequired__"
    })!;
  }

  static getPossibleOrderTypes(product: ProductData, configuratorType: ConfiguratorType): OrderType[] {
    return Product.calcOrderType(PD.productToPD(product), configuratorType).possible;
  }

  static getPossibleOrderTypesWithUndefined(product: ProductData, configuratorType: ConfiguratorType): (OrderType | undefined)[] {
    return [undefined, ...ProductLogic.getPossibleOrderTypes(product, configuratorType)];
  }

  static calcRemoteChannelCount(product: ProductData): RemoteChannelCount {
    if (Product.isSunSail(product)) {
      return SunSailLogic.calcRemoteChannelCount(product);
    } else if (Product.isRaff(product)) {
      return RaffLogic.calcRemoteChannelCount(product);
    }
    return {
      motorChannels: 0,
      totalChannels: 0,
      sunWindSensorCount: 0,
      motorRemoteType: undefined
    };
  }

  static addRemoteChannels(a: RemoteChannelCount | undefined, b: RemoteChannelCount | undefined): RemoteChannelCount {
    const rc: RemoteChannelCount = {
      motorChannels: (a?.motorChannels || 0) + (b?.motorChannels || 0),
      totalChannels: (a?.totalChannels || 0) + (b?.totalChannels || 0),
      sunWindSensorCount: (a?.sunWindSensorCount || 0) + (b?.sunWindSensorCount || 0),
      motorRemoteType: a?.motorRemoteType || b?.motorRemoteType
    }
    return rc
  }

  static hasMotor(productData: ProductData) {
    if (Product.isSunSail(productData)) {
      return MotorLogic.hasMotor(productData.sunSailType);
    } else if (Product.isRaff(productData)) {
      return RaffLogic.hasMotor(productData.raffType);
    }
    return false;
  }

  static hasMotorPD(pd: IProductDescriptor) {
    if (PD.isSail(pd)) {
      return MotorLogic.hasMotor(pd.sunSailType);
    } else if (PD.isRaff(pd)) {
      return RaffLogic.hasMotor(pd.raffType);
    }
    return false;
  }
}